import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useRegionPayoutGet } from 'actions/region-payout';
import {
  GRADE_RANGES,
  TIERS,
  COURSE_ORDER,
  VALIDITY_MODE,
  VIEW_MODE,
  regionChannel,
  ratioLabel,
} from './constants';
import { cellKey, notify, orderByReference, preferWindow } from './utils';

// Owns the region-payout fetch and everything derived from the returned rows. The
// screen owns the filter *state*; this hook turns (rows + filters) into the
// courses / regions / grid data the UI renders, and exposes fetchPayouts so the
// screen can refetch after a write.
export const useRegionPayoutData = ({
  mode,
  course,
  classRatio,
  activeRegion,
  channel,
  validityMode,
  asOfDate,
}) => {
  const dispatch = useDispatch();
  const { get: getPayouts, data, isProcessing, error } = useRegionPayoutGet();

  // Bulk mode needs every window (not just today's) so overlap detection is accurate.
  const effectiveValidityMode =
    mode === VIEW_MODE.BULK ? VALIDITY_MODE.ALL : validityMode;

  const query = useMemo(() => {
    const q = { validity_mode: effectiveValidityMode };
    if (effectiveValidityMode === VALIDITY_MODE.AS_OF && asOfDate)
      q.as_of_date = asOfDate;
    return q;
  }, [effectiveValidityMode, asOfDate]);

  // Unique store key per filter combo so the data refreshes when filters change.
  const cacheKey = `region-payout|${effectiveValidityMode}|${asOfDate || ''}`;

  const fetchPayouts = useCallback(() => {
    // "As of date" only fetches once a date is picked.
    if (effectiveValidityMode === VALIDITY_MODE.AS_OF && !asOfDate) return;
    getPayouts(cacheKey, query);
  }, [getPayouts, cacheKey, query, effectiveValidityMode, asOfDate]);

  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  useEffect(() => {
    if (error) notify(dispatch, error || 'Failed to load region payouts.');
  }, [error, dispatch]);

  const rows = useMemo(() => (data && Array.isArray(data.rows) ? data.rows : []), [data]);

  // Courses / grade ranges / tiers / ratios are all derived from the fetched rows
  // (ordered by the known references, unknowns appended) rather than hardcoded.
  const courses = useMemo(() => {
    const seen = new Set();
    rows.forEach(row => seen.add(row.course));
    return orderByReference(seen, COURSE_ORDER);
  }, [rows]);

  const regions = useMemo(() => {
    const seen = new Set();
    rows.forEach(row => {
      if (row.course === course) seen.add(row.region);
    });
    return Array.from(seen).sort();
  }, [rows, course]);

  // Region tabs shown for the selected delivery channel (offline = centers).
  const visibleRegions = useMemo(
    () => regions.filter(r => regionChannel(r) === channel),
    [regions, channel],
  );

  const gradeRanges = useMemo(() => {
    const seen = new Set();
    rows.forEach(row => {
      if (row.course === course) seen.add(row.grade_range);
    });
    return orderByReference(seen, GRADE_RANGES);
  }, [rows, course]);

  const tiers = useMemo(() => {
    const seen = new Set();
    rows.forEach(row => {
      if (row.course === course) seen.add(row.enrollment_type);
    });
    return orderByReference(seen, TIERS);
  }, [rows, course]);

  const ratioOptions = useMemo(() => {
    const ratios = new Set();
    rows.forEach(row => {
      if (row.course === course) ratios.add(row.class_ratio);
    });
    return Array.from(ratios)
      .sort((a, b) => a - b)
      .map(r => ({ value: r, label: ratioLabel(r) }));
  }, [rows, course]);

  // One representative window per region|grade|tier cell, plus a count for the badge.
  const cellData = useMemo(() => {
    const byKey = {};
    rows.forEach(row => {
      if (row.course !== course || row.class_ratio !== classRatio) return;
      const key = cellKey(row.region, row.grade_range, row.enrollment_type);
      (byKey[key] = byKey[key] || []).push(row);
    });
    const out = {};
    Object.keys(byKey).forEach(key => {
      const windows = byKey[key];
      out[key] = { row: windows.reduce(preferWindow), count: windows.length };
    });
    return out;
  }, [rows, course, classRatio]);

  // All windows for one cell (used by bulk overlap detection).
  const windowsForCell = useCallback(
    (region, gradeRange, tier) =>
      rows.filter(
        row =>
          row.course === course &&
          row.class_ratio === classRatio &&
          row.region === region &&
          row.grade_range === gradeRange &&
          row.enrollment_type === tier,
      ),
    [rows, course, classRatio],
  );

  // Grade ranges / tiers that actually have a cell for the active region + ratio.
  // Fully-empty rows/columns are dropped from the grid.
  const activePresence = useMemo(() => {
    const grades = new Set();
    const cols = new Set();
    rows.forEach(row => {
      if (row.course !== course || row.class_ratio !== classRatio) return;
      if (row.region !== activeRegion) return;
      grades.add(row.grade_range);
      cols.add(row.enrollment_type);
    });
    return { grades, cols };
  }, [rows, course, classRatio, activeRegion]);

  const visibleGradeRanges = useMemo(
    () => gradeRanges.filter(g => activePresence.grades.has(g)),
    [gradeRanges, activePresence],
  );

  const visibleTiers = useMemo(() => tiers.filter(t => activePresence.cols.has(t)), [
    tiers,
    activePresence,
  ]);

  return {
    rows,
    isProcessing,
    fetchPayouts,
    courses,
    visibleRegions,
    ratioOptions,
    cellData,
    windowsForCell,
    visibleGradeRanges,
    visibleTiers,
  };
};
